// src/pages/kanban/components/TarjetaKanban.tsx
import { Card, Badge, Avatar } from '@/components/ui';
import { SimpleTooltip } from '@/components/ui/Tooltip';
import {
  MessageSquare,
  Paperclip,
  Calendar,
  Building2,
  Folder,
  AlertCircle,
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { TarjetaKanban as TarjetaKanbanType } from '@/types/kanban';
import { cn } from '@/lib/utils';

interface Props {
  tarjeta: TarjetaKanbanType;
  tableroId: string;
  tamano?: 'compacto' | 'normal' | 'expandido';
  onDragStart: (e: React.DragEvent, tarjeta: TarjetaKanbanType) => void;
}

const PRIORIDAD_COLORES: Record<string, string> = {
  CRITICA: 'border-l-red-600',
  ALTA: 'border-l-orange-500',
  MEDIA: 'border-l-yellow-500',
  BAJA: 'border-l-green-500',
  MUY_BAJA: 'border-l-gray-400',
};

const PRIORIDAD_BADGES: Record<string, { variant: 'default' | 'primary' | 'danger' | 'success' | 'warning' | 'info' | 'purple' | 'orange'; label: string }> = {
  CRITICA: { variant: 'danger', label: 'Critica' },
  ALTA: { variant: 'orange', label: 'Alta' },
  MEDIA: { variant: 'warning', label: 'Media' },
  BAJA: { variant: 'success', label: 'Baja' },
  MUY_BAJA: { variant: 'default', label: 'Muy baja' },
};

export function TarjetaKanban({ tarjeta, tableroId, tamano = 'normal', onDragStart }: Props) {
  const handleDragStart = (e: React.DragEvent) => {
    e.dataTransfer.setData(
      'application/json',
      JSON.stringify({
        entidad_tipo: tarjeta.tipo,
        entidad_id: tarjeta.id,
        tablero_id: tableroId,
      })
    );
    e.dataTransfer.effectAllowed = 'move';
    onDragStart(e, tarjeta);
  };

  const prioridadColor = tarjeta.color || (tarjeta.prioridad ? PRIORIDAD_COLORES[tarjeta.prioridad] : 'border-l-gray-300');
  const prioridadBadge = tarjeta.prioridad ? PRIORIDAD_BADGES[tarjeta.prioridad] : null;

  const isCompacto = tamano === 'compacto';
  const isExpandido = tamano === 'expandido';

  return (
    <Card
      draggable
      onDragStart={handleDragStart}
      className={cn(
        'cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow border-l-4',
        prioridadColor,
        isCompacto ? 'p-2' : 'p-3'
      )}
    >
      {/* Header: Tipo badge y numero/proyecto */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <Badge variant={tarjeta.tipo === 'ticket' ? 'primary' : 'info'} className="flex-shrink-0 text-xs">
            {tarjeta.tipo === 'ticket' ? 'Ticket' : 'Actividad'}
          </Badge>
          {tarjeta.numero && (
            <span className="text-xs text-gray-500 font-mono">#{tarjeta.numero}</span>
          )}
        </div>
        {prioridadBadge && !isCompacto && (
          <Badge variant={prioridadBadge.variant} className="flex-shrink-0 text-xs">
            {prioridadBadge.label}
          </Badge>
        )}
      </div>

      {/* Titulo */}
      <h4 className={cn(
        'font-medium text-gray-900',
        isCompacto ? 'text-sm line-clamp-1' : 'text-sm line-clamp-2'
      )}>
        {tarjeta.titulo}
      </h4>

      {/* Descripcion (solo expandido) */}
      {isExpandido && tarjeta.descripcion_corta && (
        <p className="text-xs text-gray-500 mt-1 line-clamp-2">
          {tarjeta.descripcion_corta}
        </p>
      )}

      {/* Info adicional */}
      {!isCompacto && (
        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
          {tarjeta.tipo === 'ticket' && tarjeta.cliente && (
            <SimpleTooltip content={tarjeta.cliente}>
              <div className="flex items-center gap-1 truncate max-w-[120px]">
                <Building2 className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{tarjeta.cliente}</span>
              </div>
            </SimpleTooltip>
          )}
          {tarjeta.tipo === 'actividad' && tarjeta.proyecto && (
            <SimpleTooltip content={tarjeta.proyecto}>
              <div className="flex items-center gap-1 truncate max-w-[120px]">
                <Folder className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{tarjeta.proyecto}</span>
              </div>
            </SimpleTooltip>
          )}
          {tarjeta.fecha_limite && (
            <SimpleTooltip content="Fecha limite">
              <div className={cn(
                'flex items-center gap-1',
                new Date(tarjeta.fecha_limite) < new Date() && 'text-red-500'
              )}>
                <Calendar className="h-3 w-3" />
                <span>{format(new Date(tarjeta.fecha_limite), 'd MMM', { locale: es })}</span>
                {new Date(tarjeta.fecha_limite) < new Date() && (
                  <AlertCircle className="h-3 w-3" />
                )}
              </div>
            </SimpleTooltip>
          )}
        </div>
      )}

      {/* Etiquetas */}
      {tarjeta.etiquetas.length > 0 && !isCompacto && (
        <div className="flex flex-wrap gap-1 mt-2">
          {tarjeta.etiquetas.slice(0, isExpandido ? 5 : 3).map((etiqueta) => (
            <Badge key={etiqueta} variant="default" className="text-xs px-1.5 py-0">
              {etiqueta}
            </Badge>
          ))}
          {tarjeta.etiquetas.length > (isExpandido ? 5 : 3) && (
            <Badge variant="default" className="text-xs px-1.5 py-0">
              +{tarjeta.etiquetas.length - (isExpandido ? 5 : 3)}
            </Badge>
          )}
        </div>
      )}

      {/* Footer: asignado y contadores */}
      <div className="flex items-center justify-between mt-3 pt-2 border-t">
        {tarjeta.asignado ? (
          <SimpleTooltip content={tarjeta.asignado.nombre}>
            <div>
              <Avatar
                src={tarjeta.asignado.avatar}
                alt={tarjeta.asignado.nombre}
                className="h-6 w-6"
              />
            </div>
          </SimpleTooltip>
        ) : (
          <div className="h-6" />
        )}
        <div className="flex items-center gap-3 text-gray-400">
          {tarjeta.comentarios_count > 0 && (
            <SimpleTooltip content={`${tarjeta.comentarios_count} comentarios`}>
              <div className="flex items-center gap-1">
                <MessageSquare className="h-3.5 w-3.5" />
                <span className="text-xs">{tarjeta.comentarios_count}</span>
              </div>
            </SimpleTooltip>
          )}
          {tarjeta.adjuntos_count > 0 && (
            <SimpleTooltip content={`${tarjeta.adjuntos_count} adjuntos`}>
              <div className="flex items-center gap-1">
                <Paperclip className="h-3.5 w-3.5" />
                <span className="text-xs">{tarjeta.adjuntos_count}</span>
              </div>
            </SimpleTooltip>
          )}
        </div>
      </div>
    </Card>
  );
}
